import { useQuery } from '@tanstack/react-query';
import type { TranslatableString } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';
import { queryKeys } from '@/lib/query-keys.js';

export type Option = { value: string; label: string };

type Row = { _id: string } & Record<string, unknown>;

/**
 * Blok formalari uchun tanlov ro'yxatlari.
 *
 * `enabled` bilan boshqariladi: ro'yxat FAQAT kerak bo'lganda yuklanadi.
 * Har bir blok ochilganda o'nlab kolleksiya tortilsa, konstruktor
 * sekinlashardi — xodim esa ko'p hollarda «avtomatik» rejimda qoldiradi
 * va ro'yxat umuman kerak bo'lmaydi.
 */
export function useBlockOptions(collection: string, enabled: boolean) {
  const params = { limit: 100, sort: 'title' as const };

  const query = useQuery({
    queryKey: queryKeys.content.list(collection, { ...params, purpose: 'options' }),
    enabled,
    // Tanlov ro'yxatlari kam o'zgaradi — blokdan blokka o'tganda qayta
    // yuklanmasin.
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) => api.get<ListResult<Row>>(`/${collection}`, params, signal),
  });

  const options: Option[] = (query.data?.data ?? []).map((row) => ({
    value: row._id,
    label: labelOf(row),
  }));

  return { options, isLoading: query.isLoading };
}

/**
 * Ro'yxatda ko'rsatiladigan nom.
 *
 * Kolleksiyalarda nom maydoni har xil: `title`, `name`, `fullName`.
 * Topilmasa `slug`, u ham bo'lmasa id — bo'sh qator chiqmasligi kerak,
 * aks holda xodim qaysi elementni tanlayotganini bilmasdi.
 */
function labelOf(row: Row): string {
  for (const field of ['title', 'name', 'fullName']) {
    const value = row[field];
    if (isTranslatable(value)) return value.uz || value.ru || value.en || '';
    if (typeof value === 'string' && value) return value;
  }
  return typeof row.slug === 'string' && row.slug ? row.slug : row._id;
}

function isTranslatable(value: unknown): value is TranslatableString {
  return typeof value === 'object' && value !== null && 'uz' in value;
}
